import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { notificationsApi } from '../api/notifications';
import {
  HomeIcon,
  ChartBarIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  LightBulbIcon,
  ClipboardDocumentListIcon,
  CalendarIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  TagIcon,
  Cog6ToothIcon,
  BellIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  ArrowPathIcon,
  BoltIcon,
  ChartBarSquareIcon,
  BuildingStorefrontIcon,
  ShieldCheckIcon,
  DocumentIcon,
  KeyIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import {
  HomeIcon as HomeIconSolid,
  ChartBarIcon as ChartBarIconSolid,
  BuildingOfficeIcon as BuildingOfficeIconSolid,
  UserGroupIcon as UserGroupIconSolid,
  LightBulbIcon as LightBulbIconSolid,
  ClipboardDocumentListIcon as ClipboardDocumentListIconSolid,
  CalendarIcon as CalendarIconSolid,
  TagIcon as TagIconSolid,
  Cog6ToothIcon as Cog6ToothIconSolid,
  BellIcon as BellIconSolid,
  TrashIcon as TrashIconSolid,
  ArrowDownTrayIcon as ArrowDownTrayIconSolid,
  ArrowUpTrayIcon as ArrowUpTrayIconSolid,
  DocumentTextIcon as DocumentTextIconSolid,
  CurrencyDollarIcon as CurrencyDollarIconSolid,
  ArrowPathIcon as ArrowPathIconSolid,
  BoltIcon as BoltIconSolid,
  ChartBarSquareIcon as ChartBarSquareIconSolid,
  BuildingStorefrontIcon as BuildingStorefrontIconSolid,
  ShieldCheckIcon as ShieldCheckIconSolid,
  DocumentIcon as DocumentIconSolid,
  KeyIcon as KeyIconSolid,
  UsersIcon as UsersIconSolid,
} from '@heroicons/react/24/solid';

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  iconSolid: React.ComponentType<{ className?: string }>;
  badge?: number;
}

const PremiumSidebar: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['notifications-unread-count', user?.id],
    queryFn: notificationsApi.getUnreadCount,
    refetchInterval: 30000,
    enabled: !!user?.id,
  });

  const navigation: NavItem[] = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon, iconSolid: HomeIconSolid },
    { name: 'Leads', href: '/leads', icon: ChartBarIcon, iconSolid: ChartBarIconSolid },
    { name: 'Accounts', href: '/accounts', icon: BuildingOfficeIcon, iconSolid: BuildingOfficeIconSolid },
    { name: 'Contacts', href: '/contacts', icon: UserGroupIcon, iconSolid: UserGroupIconSolid },
    { name: 'Tasks', href: '/tasks', icon: ClipboardDocumentListIcon, iconSolid: ClipboardDocumentListIconSolid },
    { name: 'Activities', href: '/activities', icon: CalendarIcon, iconSolid: CalendarIconSolid },
    { name: 'AI Insights', href: '/insights', icon: LightBulbIcon, iconSolid: LightBulbIconSolid },
    { name: 'Close Plans', href: '/close-plans', icon: DocumentTextIcon, iconSolid: DocumentTextIconSolid },
    { name: 'Billing', href: '/billing', icon: CurrencyDollarIcon, iconSolid: CurrencyDollarIconSolid },
    { name: 'Files', href: '/files', icon: DocumentIcon, iconSolid: DocumentIconSolid },
    { name: 'Tags', href: '/tags', icon: TagIcon, iconSolid: TagIconSolid },
    { 
      name: 'Notifications', 
      href: '/notifications', 
      icon: BellIcon, 
      iconSolid: BellIconSolid,
      badge: unreadCount > 0 ? unreadCount : undefined,
    },
    { name: 'Stage Movements', href: '/stage-movements', icon: ArrowPathIcon, iconSolid: ArrowPathIconSolid },
    { name: 'Meeting Analytics', href: '/meeting-analytics', icon: ChartBarSquareIcon, iconSolid: ChartBarSquareIconSolid },
    { name: 'Export', href: '/export', icon: ArrowDownTrayIcon, iconSolid: ArrowDownTrayIconSolid },
    { name: 'Import', href: '/import', icon: ArrowUpTrayIcon, iconSolid: ArrowUpTrayIconSolid },
    { name: 'Recycle Bin', href: '/recycle-bin', icon: TrashIcon, iconSolid: TrashIconSolid },
    { name: 'Custom Fields', href: '/custom-fields', icon: Cog6ToothIcon, iconSolid: Cog6ToothIconSolid },
    { name: 'Webhooks', href: '/webhooks', icon: BoltIcon, iconSolid: BoltIconSolid },
    { name: 'Settings', href: '/settings', icon: KeyIcon, iconSolid: KeyIconSolid },
    { name: 'Admin Panel', href: '/admin', icon: ShieldCheckIcon, iconSolid: ShieldCheckIconSolid },
  ];

  // Add Team View for superadmin, admin, and leadership
  if (user?.role === 'superadmin' || user?.role === 'admin' || user?.role === 'leadership') {
    navigation.push({ name: 'Team View', href: '/team-view', icon: UsersIcon, iconSolid: UsersIconSolid });
  }

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
      navigate('/login');
    }
  };

  return (
    <div className="hidden md:flex md:flex-shrink-0">
      <div className="flex flex-col w-72">
        <div className="flex flex-col flex-grow pt-6 pb-4 overflow-y-auto bg-slate-900 border-r border-slate-800">
          {/* Logo Section */}
          <div className="flex items-center flex-shrink-0 px-6 mb-8">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center border border-slate-700">
                <span className="text-white font-bold text-xl">PS</span>
              </div>
              <div>
                <h1 className="text-white text-xl font-bold tracking-tight">Pulp Strategy</h1>
                <p className="text-slate-400 text-xs font-medium">CRM Platform</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 space-y-1">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              const Icon = isActive ? item.iconSolid : item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'bg-slate-800 text-white shadow-md border border-slate-700'
                      : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
                  }`}
                >
                  <Icon className={`mr-3 flex-shrink-0 h-5 w-5 ${isActive ? 'text-white' : 'text-slate-400 group-hover:text-white'}`} />
                  <span className="flex-1">{item.name}</span>
                  {item.badge && (
                    <span className={`ml-3 inline-flex items-center justify-center px-2 py-0.5 rounded-md text-xs font-semibold ${
                      isActive ? 'bg-slate-700 text-white' : 'bg-slate-800 text-slate-300'
                    }`}>
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User Section */}
          <div className="flex-shrink-0 flex border-t border-slate-800 p-4">
            <div className="w-full space-y-2">
              <div className="flex items-center w-full group">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center border border-slate-700">
                    <UserCircleIcon className="h-6 w-6 text-slate-300" />
                  </div>
                </div>
                <div className="ml-3 flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
                  <p className="text-xs text-slate-400 capitalize truncate">{user?.role} • Online</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="ml-2 flex-shrink-0 p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-all duration-200"
                  title="Logout"
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiumSidebar;
